import { Injectable } from '@angular/core';
import { VerticalNavigationComponent } from './shared/vertical-navigation/vertical-navigation.component';
import { BehaviorSubject, filter, Observable, skip, Subscription, takeUntil, tap } from 'rxjs';
import { SvcDrawerComponent } from '@solvace/component/svc-drawer';

@Injectable()
export class SvcPrivateLayoutService {
  private _ready = new BehaviorSubject<boolean>(false);
  public ready$: Observable<void> = this._ready.pipe(filter((value) => value)) as any;

  private _verticalNavigationComp: VerticalNavigationComponent;
  private _navigationRetractingSubscription: Subscription;
  private _navigationOpeningSubscription: Subscription;
  public get menuRectracting$(): Observable<boolean> {
    return this._verticalNavigationComp?.rectracting$;
  }

  private _drawerComp: SvcDrawerComponent;
  private _drawerToggleSubscription: Subscription;

  public setVerticalNavigationComponent(component: VerticalNavigationComponent): void {
    this._verticalNavigationComp = component;
    this._navigationRetractingSubscription?.unsubscribe();
    this._navigationOpeningSubscription?.unsubscribe();
    const drawer = () => this._drawerComp;
    if (!this._ready.value) this._ready.next(true);
    this._navigationRetractingSubscription = component.rectracting$
      .pipe(
        // BehaviorSubject replays the current retraction on (re)registration —
        // skip that snapshot and only react to actual user toggles.
        skip(1),
        filter((retracted) => !retracted && drawer()?.isOpened),
        tap(() => this._drawerComp?.toggle())
      )
      .subscribe();
    // AB#72614 — rectracting$ never fires while the menu is in 'over' mode (mobile), so the
    // mutual exclusion also listens to opening$: the hamburger opening the nav closes an open
    // filter drawer.
    this._navigationOpeningSubscription = component.opening$
      .pipe(
        skip(1),
        filter((opened) => opened && !!drawer()?.isOpened),
        tap(() => this._drawerComp?.toggle(false))
      )
      .subscribe();
  }

  public setCurrentDrawerRendered(component: SvcDrawerComponent): void {
    this._drawerComp = component;
    this._drawerToggleSubscription?.unsubscribe();
    const navigation = () => this._verticalNavigationComp;
    this._drawerToggleSubscription = component?.isOpened$
      .pipe(
        takeUntil(component?.destroy$),
        filter((opened) => opened),
        tap(() => {
          if (navigation()?.mode === 'over' && navigation()?.opened) {
            navigation().toggle();
          } else if (navigation()?.mode === 'side' && !(navigation()?.retracted ?? true)) {
            navigation().toggleRetraction();
          }
        })
      )
      .subscribe();
  }

  public retractMenu(): void {
    if (!this._verticalNavigationComp?.retracted) {
      this._verticalNavigationComp.toggleRetraction();
    }
  }

  public expandMenu(): void {
    if (this._verticalNavigationComp?.retracted) {
      this._verticalNavigationComp.toggleRetraction();
    }
  }

  public destroy(): void {
    this._navigationRetractingSubscription?.unsubscribe();
    this._navigationOpeningSubscription?.unsubscribe();
    this._drawerToggleSubscription?.unsubscribe();
  }
}
